/**
 * Product card behaviour: quick add, and the save toggle.
 *
 * Delegated from the document rather than built as a custom element per card.
 * A listing page renders forty of these, and forty element instances to watch
 * two clicks is waste — the card itself is static markup and stays that way.
 *
 * Both controls work without this file. The quick-add form posts to
 * /cart/add.js and the browser follows it; this only upgrades the response to
 * an in-place confirmation. The heart needs the wishlist, which is A24 — until
 * then it toggles its own state and announces it, and nothing stores it.
 */

/**
 * interactions.md §3.9: the label becomes ✓ Added for 1.2s, then reverts. No
 * toast, no confetti. The A11 board holds it for 2200ms; the spec wins on
 * state, and one confirmation dwell across the site beats two.
 */
const CONFIRM_MS = 1200;
const WISHLIST_KEY = 'stella:wishlist';

const timers = new WeakMap();

async function quickAdd(form) {
  const button = form.querySelector('button');
  const label = form.querySelector('[data-quick-label]');
  if (!button || !label) return;

  clearError(form);
  button.disabled = true;

  try {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: form.querySelector('[name="id"]').value,
        quantity: 1
      })
    });

    if (!response.ok) throw new Error(String(response.status));

    confirm(button, label);
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  } catch {
    // Inline, under the button. Never a browser alert — interactions.md §4.
    showError(form);
  } finally {
    button.disabled = false;
  }
}

function confirm(button, label) {
  const original = label.dataset.rest || label.textContent;
  label.dataset.rest = original;
  label.textContent = label.dataset.confirmed || '✓ Added';
  button.setAttribute('data-added', '');

  clearTimeout(timers.get(button));
  timers.set(
    button,
    setTimeout(() => {
      label.textContent = original;
      button.removeAttribute('data-added');
    }, CONFIRM_MS)
  );
}

function showError(form) {
  if (form.querySelector('.card__error')) return;
  const message = document.createElement('p');
  message.className = 'card__error';
  message.setAttribute('role', 'alert');
  message.textContent = form.dataset.error || 'That did not go through. Try again.';
  form.append(message);
}

function clearError(form) {
  form.querySelector('.card__error')?.remove();
}

function persistSave(handle, saved) {
  if (!handle) return;
  try {
    const stored = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    const list = Array.isArray(stored) ? stored : [];
    const at = list.findIndex((entry) => (typeof entry === 'string' ? entry : entry.h) === handle);
    if (saved && at === -1) list.push({ h: handle, t: Date.now(), n: '' });
    if (!saved && at !== -1) list.splice(at, 1);
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  } catch {
    /* Saving still works for this visit when browser storage is unavailable. */
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-quick-add]');
  if (!form) return;
  event.preventDefault();
  quickAdd(form);
});

document.addEventListener('click', (event) => {
  const save = event.target.closest('[data-save]');
  if (!save) return;

  const next = save.getAttribute('aria-pressed') !== 'true';
  save.setAttribute('aria-pressed', String(next));

  // A24 owns persistence and A25 owns the save gate. This only reports.
  const handled = !save.dispatchEvent(
    new CustomEvent('stella:save-toggle', {
      bubbles: true,
      cancelable: true,
      detail: { productId: save.dataset.productId, saved: next }
    })
  );
  if (!handled) persistSave(save.dataset.handle, next);
});
